<template>
  <v-layout>
    <!-- Loading -->
    <v-layout v-if="!isExercisesFetched" column justify-center align-center class="mt-5">
      <v-progress-circular :size="50" color="primary" indeterminate></v-progress-circular>
      <p class="subheading-1 mt-5 primary--text font-weight-bold">Loading...</p>
    </v-layout>
    <!-- If exercises were downloaded -->
    <v-container v-else class="ma-0 pa-0">
      <v-layout xs12 class="mb-5">
        <!-- Exercise Details (Left) -->
        <v-flex>
          <h3 class="font-weight-medium">{{ selectedExercise.exerciseTitle }}</h3>
          <p class="caption mt-3">{{ selectedExercise.exerciseShort }}</p>
          <div
            class="h-chip caption mb-4"
            :class="exDifficulty[selectedExercise.difficulty]"
          >Difficulty: {{ exDifficulty[selectedExercise.difficulty] }}</div>
          <v-tooltip right max-width="220">
            <template v-slot:activator="{ on }">
              <v-icon class="ml-2" small v-on="on" color="grey">mdi-information</v-icon>
            </template>
            <span
              class="caption"
            >Points per question depends upon the Difficulty of the Exercise, Easy = 1 Point, Medium = 2 Points, Hard = 3 Points</span>
          </v-tooltip>
          <v-layout column>
            <div class="caption db--text mr-5">
              <v-icon small class="mr-1">mdi-clock-outline</v-icon>
              {{ selectedExercise.createdAt | dateFormat }}
            </div>
            <div class="caption db--text d-block mt-3">
              <v-icon small class="mr-1">mdi-clipboard-outline</v-icon>
              {{ selectedExercise.questions.length }} Questions
            </div>
          </v-layout>
        </v-flex>
        <!-- //// Exercise Details (Left) -->
        <!-- Exercise Links (Right) -->
        <v-flex class="text-right">
          <v-flex v-if="selectedExercise.briefLink">
            <v-btn
              :href="selectedExercise.briefLink"
              target="_blank"
              class="r-btn caption text-capitalize db white--text"
              depressed
            >
              <v-icon small left>mdi-download</v-icon>Read Brief
            </v-btn>
          </v-flex>
          <v-btn
            v-if="selectedExercise.youtubeLink"
            :href="selectedExercise.youtubeLink"
            target="_blank"
            class="r-btn caption text-capitalize red white--text dr white--text mt-3"
            depressed
          >
            <v-icon small left>mdi-youtube</v-icon>Watch Video
          </v-btn>
        </v-flex>
        <!-- /// Exercise Links (Right) -->
      </v-layout>
      <v-layout class="text-center red--text">{{error }}</v-layout>
      <!-- Quiz Interactive Start -->
      <v-layout v-if="!quizSubmitted.status" class="text-center">
        <v-flex xs12 v-if="!quizStarted">
          <h4 class="db--text mt-5 font-weight-medium">You can start with the Exercise</h4>
          <v-btn
            @click="quizStarted = true"
            class="r-btn mt-4 db white--text caption"
            depressed
          >Start Exercise</v-btn>
        </v-flex>
        <v-layout justify-center v-else class="mt-4">
          <v-flex xs8>
            <h4 class="my-4">{{ currentQues + 1 }} / {{ selectedExercise.questions.length }}</h4>
            <v-progress-linear class="mb-4" :value="progressBarValue" height="6" rounded striped></v-progress-linear>
            <div v-if="!quizSubmissionLoader" class="text-center">
              <h4 class="db--text font-weight-medium my-4">{{ currentQuestion.ques }}</h4>
              <div
                v-for="op in currentQuestion.ops"
                :key="op.opId"
                class="customRadio pa-2 caption"
              >
                <input type="radio" v-model="selectedOp" :value="op.opId" :id="op.opId">
                <label :for="op.opId">{{ op.op }}</label>
              </div>
            </div>
            <v-btn
              :loading="quizSubmissionLoader"
              v-if="selectedOp"
              @click="continueQuiz"
              class="r-btn caption text-capitalize gr white--text mt-5"
            >{{ lastQues ? 'Submit' :'Continue' }}</v-btn>
          </v-flex>
        </v-layout>
      </v-layout>
      <v-layout v-else class="text-center">
        <v-flex class="mt-5">
          <h2
            class="font-weight-regular"
          >You scored {{ quizSubmitted.score }} / {{ selectedExercise.questions.length }}</h2>
          <p>{{ quizSubmitted.points ? quizSubmitted.points + ' points have been added to your profile!' : 'No points were added to your profile'}}</p>
          <v-btn
            depressed
            @click="$router.push('/exercises')"
            class="r-btn db white--text caption"
          >Go to Exercises</v-btn>
        </v-flex>
      </v-layout>
      <!-- /// Quiz Interactive Start -->
    </v-container>
  </v-layout>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
export default {
  props: ["exerciseId"],
  data() {
    return {
      exDifficulty: {
        1: "Easy",
        2: "Medium",
        3: "Hard"
      },
      selectedExercise: {},
      error: "",
      exercises: [],
      quizStarted: false,
      currentQues: 0,
      selectedOp: null,
      answers: [],
      quizSubmissionLoader: false,
      quizSubmitted: {
        status: false,
        score: 0,
        points: 0
      }
    };
  },
  methods: {
    ...mapActions(["fetchExercises", "submitExercise", "fetchUserDetails"]),
    ...mapMutations([
      "switchExercisesFetch",
      "addExerciseId",
      "resetScoreboard",
      "updatePostExercise"
    ]),
    async continueQuiz() {
      this.answers.push({
        quesId: this.currentQuestion.quesId,
        opId: this.selectedOp
      });
      if (!this.lastQues) {
        this.currentQues = this.currentQues + 1;
        this.selectedOp = null;
      } else {
        this.quizSubmissionLoader = true;
        const resp = await this.submitExercise({
          answers: this.answers,
          exerciseId: this.selectedExercise.exerciseId
        });
        if (resp.data.success) {
          this.quizSubmitted.score = resp.data.score;
          this.quizSubmitted.points = resp.data.points;
          this.quizSubmitted.status = true;
          this.addExerciseId(this.selectedExercise.exerciseId);
          this.updatePostExercise({
            points: resp.data.points,
            ans: resp.data.score,
            ques: this.selectedExercise.questions.length
          });
        } else {
          this.error = "Something Went wrong";
          setTimeout(() => {
            this.$router.push("/exercises");
          }, 3000);
        }
      }
    }
  },
  computed: {
    ...mapGetters(["getExercises", "isExercisesFetched", "searchListed"]),
    progressBarValue() {
      return (
        (100 / this.selectedExercise.questions.length) * (this.currentQues + 1)
      );
    },
    currentQuestion() {
      return this.selectedExercise.questions[this.currentQues];
    },
    lastQues() {
      return this.currentQues + 1 === this.selectedExercise.questions.length;
    }
  },
  created() {
    // whether exercises are fetched from the db or not
    if (!this.isExercisesFetched) {
      this.$router.push("/exercises");
    }
    this.exercises = this.getExercises;
    const selectedExercise = this.exercises.find(
      ex => ex.exerciseId === this.exerciseId
    );
    if (selectedExercise) {
      this.selectedExercise = selectedExercise;
    } else {
      this.$router.push("/exercises");
    }
  },
  beforeRouteLeave(to, from, next) {
    if (!this.isExercisesFetched) return next();
    if (!this.quizStarted) return next();
    if (this.quizSubmitted.status) {
      if (this.searchListed) this.resetScoreboard();
      this.switchExercisesFetch();
      return next();
    }
    if (this.error) {
      this.fetchUserDetails();
      this.switchExercisesFetch();
      return next();
    }
    if (confirm("Are you sure, you want to quit?")) next();
  }
};
</script>

<style>
.ma-0 {
  margin: 0 !important;
}
.h-chip {
  display: inline-block;
  padding: 5px 15px;
  border-radius: 20px;
}
.h-chip.Easy {
  background-color: #e6f1d4;
  color: #7db900;
}
.h-chip.Medium {
  background-color: rgb(253, 238, 206);
  color: #f3ac13;
}
.h-chip.Hard {
  background-color: rgb(255, 209, 216);
  color: #ea2340;
}

.customRadio {
  display: inline-block;
}

@media (max-width: 630px) {
  .customRadio {
    display: block;
  }
}

.customRadio input {
  display: none;
}
.customRadio label {
  text-align: center;
  display: block;
  padding: 5px 12px;
  border-radius: 30px;
  color: #2d2d44;
  border: 1px solid #2d2d44;
}
.customRadio input:checked + label {
  background-color: #2d2d44;
  color: #fff;
}
</style>
